import React, { useState, useEffect, useRef } from "react";
import { useForm } from "@mantine/form";
import { countriesData, industriesData } from "../components/Onboarding/data";
import fetchApi from "../components/Lib/api";
import { cleanText } from "../components/Lib/profanityCleaner";
import styled, { keyframes } from "styled-components";
import { DatePickerInput } from "@mantine/dates";
import useUserStore from "../stores/userStore";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";


const morphRotate = keyframes`
  0% {
    border-radius: 20%;
    transform: rotate(0deg);
  }
  50% {
    border-radius: 50%;
    transform: rotate(180deg);
  }
  100% {
    border-radius: 20%;
    transform: rotate(360deg);
  }
`;

const LoaderS = styled.div`
  width: 80px;
  height: 80px;
  background-color: var(--color-button-accent);
  margin: 0 auto;
  animation: ${morphRotate} 2s infinite;
`;

const Container = styled.div`
  text-align: center;
  padding: 2rem;
`;

const Message = styled.p`
  margin-top: 1.5rem;
`;

const FormContainer = styled.div`
  max-width: 700px;
  margin: 2rem auto;
  padding: 2.5rem 2rem;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(30,27,75,0.08);
  overflow-y: auto;
  min-height: 80vh;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
`;
const HeaderContainer2 = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.6rem;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Caption = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4338ca;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 20px;
  color: ${(props) => (props.darkMode ? "white" : "#1E1B4B")};
  margin: 0;
  margin-bottom: 0.1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: ${(props) => (props.darkMode ? "#666" : "#6B7280")};
  font-size: 12px;
  margin: 0;
  margin-bottom: 0.3rem;
`;
const Description2 = styled.p`
  color: ${(props) => (props.darkMode ? "#666" : "#6B7280")};
  font-size: 12px;
  margin: 0;
  margin-bottom: 0.2rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.7rem;
  margin-bottom: 0.7rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const Label = styled.label`
  font-size: 11px;
  color: ${(props) => (props.darkMode ? "white" : "#1E1B4B")};
  font-weight: 500;
  margin-bottom: 0.1rem;
`;

const Input = styled.input`
  padding: 0.4rem 0.6rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  color: #6b7280;
  width: 100%;
  &::placeholder {
    color: #9ca3af;
  }
  &:focus {
    outline: none;
    border-color: #4338CA;
    box-shadow: 0 0 0 1.5px #4338CA33;
  }
`;

const StyledTextArea = styled.textarea`
  padding: 0.4rem 0.6rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  color: #6b7280;
  width: 100%;
  background: transparent;
  &::placeholder {
    color: #9ca3af;
  }
  &:focus {
    outline: none;
    border-color: #4338CA;
    box-shadow: 0 0 0 1.5px #4338CA33;
  }
`;

const Select = styled.select`
  padding: 0.4rem 0.6rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  color: #6b7280;
  width: 100%;
  background: white;
  appearance: none;
  background-image: url("path-to-your-chevron-down.svg");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  &:focus {
    outline: none;
    border-color: #4338CA;
    box-shadow: 0 0 0 1.5px #4338CA33;
  }
`;

const SecurityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
`;

const SecurityCard = styled.div`
  background: #f3f4f6;
  border: 1px solid ${(props) => (props.selected ? "#4338CA" : "transparent")};
  border-radius: 12px;
  padding: 0.8rem;
  cursor: pointer;
  
  transition: border-color 0.2s ease;
  min-width: 0;
  min-height: 0;
  &:hover {
    border-color: #4338ca;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  img {
    width: 20px;
    height: 20px;
  }
`;

const CardTitle = styled.h3`
  color: #1e1b4b;
  font-size: 13px;
  margin: 0;
`;

const CardDescription = styled.p`
  font-size: 11px;
  margin: 0;
  line-height: 1.4;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid ${(props) => (props.darkMode ? "#333" : "#E5E7EB")};
`;

const StepCounter = styled.span`
  color: ${(props) => (props.darkMode ? "#666" : "#6B7280")};
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #4338CA;
    color: white;
    border: none;
    
    &:hover {
      background: #3730A3;
    }
  `
      : `
    background: transparent;
    color: #4338CA;
    border: none;
    
    &:hover {
      background: #F8FAFC;
    }
  `}
`;

const PricingContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: start;
`;

const EmailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CheckboxGroup = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-top: 1rem;
`;

const Checkbox = styled.input`
  cursor: pointer;
`;

const CheckboxLabel = styled.span`
  color: #666;
  font-size: 14px;
`;

const HelperText = styled.p`
  color: #666;
  font-size: 14px;
  margin: 1rem 0;
  line-height: 1.5;
`;

// DatePicker input style override
const datePickerInputStyle = {
  padding: '0.4rem 0.6rem',
  fontSize: 12,
  color: '#6b7280',
  width: '100%',
  boxSizing: 'border-box',
};

export const LoadingScreen = () => {
  const messages = [
    "We are setting up your workspace...",
    "Your data is being optimized...",
    "Almost there...",
  ];

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentMessageIndex((prevIndex) =>
        prevIndex === messages.length - 1 ? 0 : prevIndex + 1
      );
    }, 30000);

    return () => clearInterval(timer);
  }, [messages.length]);

  return (
    <Container>
      <LoaderS />
      <Message>{messages[currentMessageIndex]}</Message>
    </Container>
  );
};

const MultiStepForm = () => {
   const { i18n } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState(
      localStorage.getItem("appLanguage") || i18n.language || "en"
    );
  
    useEffect(() => {
      // Apply the language on component mount
      i18n.changeLanguage(selectedLanguage);
    }, [selectedLanguage, i18n]);
  
  // Remove step logic
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [roadmapChunks, setRoadmapChunks] = useState([]);
 
  const {
    user: { enable_ai: ai },
  } = useUserStore((state) => state.user);

  const stepContainerRef = useRef(null);
  const {t}=useTranslation();
  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      industry: "",
      stage: "",
      roadmap: ai ? "" : "Manual",
      project_type: "",
      country: "",
      city: "",
      website: "",
      linkedin: "",
      social_media: "",
      public_listed: true,
      start_date: null,
      end_date: null,
      details: "",
    },
    validate: {
      name: (value) =>
        !value || value.trim() === "" ? "Name is required" : null,
      description: (value) =>
        !value || value.trim() === "" ? "Description is required" : null,
      industry: (value) => (!value ? "Industry is required" : null),
      country: (value) => (!value ? "Country is required" : null),
      city: (value) =>
        !value || value.trim() === "" ? "City is required" : null,
      website: (value) => {
        if (value && !/^https?:\/\//i.test(value)) {
          return "Please enter a valid URL (starting with http:// or https://)";
        }
        return null;
      },
      linkedin: (value) => {
        if (value && !/^https?:\/\//i.test(value)) {
          return "Please enter a valid URL (starting with http:// or https://)";
        }
        return null;
      },
      social_media: (value) => {
        if (value && !/^https?:\/\//i.test(value)) {
          return "Please enter a valid URL (starting with http:// or https://)";
        }
        return null;
      },
      roadmap: (value) =>
        !value || value.trim() === "" ? "Please select a roadmap option" : null,
      start_date: (value) => (!value ? "Start date is required" : null),
      end_date: (value, values) => {
        if (!value) return "End date is required";
        if (values.start_date && value) {
          const diffTime = new Date(value) - new Date(values.start_date);
          const diffDays = diffTime / (1000 * 60 * 60 * 24);
          if (diffDays < 7) {
            return "End date must be at least 7 days after start date";
          }
        }
        return null;
      },
    },
  });

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  // --- Define all renderStep functions here ---
  // --- Define all renderStep functions here ---
  const renderStepOne = () => (
    
    <>
      <HeaderContainer style={{ marginBottom: "10px" }}>
        <HeaderContent>
          <Title>{t("create_project.step_one.title")}</Title>
          <Description>{t("create_project.step_one.description")}</Description>
        </HeaderContent>
      </HeaderContainer>
      <FormGrid>
        <FormField style={{ gridColumn: "1 / -1" }}>
          <Label>{t("create_project.step_one.label_name")}</Label>
          <FocusInput
            type="text"
            placeholder={t("create_project.step_one.placeholder_name")}
            {...form.getInputProps("name")}
          />
          {form.errors.name && (
            <div style={{ color: "red", marginTop: "0.5rem" }}>
              {form.errors.name}
            </div>
          )}
        </FormField>
      </FormGrid>
    </>
  );
  
  const renderStepTwo = () => (
    <>
      <HeaderContainer2>
        <HeaderContent>
          <Title style={{ marginTop: "2rem" }}>{t("create_project.step_two.title")}</Title>
          <Description2>{t("create_project.step_two.description")}</Description2>
        </HeaderContent>
      </HeaderContainer2>
      <FormGrid>
        <FormField style={{ gridColumn: "1 / -1" }}>
          <Label>{t("create_project.step_two.label_description")}</Label>
          <FocusTextArea
            rows={4}
            maxLength={500}
            placeholder={t("create_project.step_two.placeholder_description")}
            {...form.getInputProps("description")}
          />
          <div
            style={{
              color: "gray",
              fontSize: "10px",
              marginLeft: "auto",
              marginTop: "0.2rem",
            }}
          >
            {form.values.description.length} / 500
          </div>
          {form.errors.description && (
            <div style={{ color: "red", marginTop: "0.5rem" }}>
              {form.errors.description}
            </div>
          )}
        </FormField>
      </FormGrid>
  
      <HeaderContainer2>
        <HeaderContent>
          <Label>{t("create_project.step_two.label_details")}</Label>
          <Description2 style={{ marginTop: "0.2rem" }}>
            {t("create_project.step_two.description_details")}
          </Description2>
        </HeaderContent>
      </HeaderContainer2>
      <FormField style={{ gridColumn: "1 / -1" }}>
        <FocusTextArea
          rows={4}
          maxLength={7000}
          placeholder={t("create_project.step_two.placeholder_details")}
          {...form.getInputProps("details")}
        />
        <div
          style={{
            color: "gray",
            fontSize: "10px",
            marginLeft: "auto",
            marginTop: "0.2rem",
          }}
        >
          {form.values.details.length} / 7000
        </div>
      </FormField>
    </>
  );
  
  const renderStepThree = () => (
    <>
      <HeaderContainer style={{ marginBottom: "1rem", marginTop: "2rem" }}>
        <HeaderContent>
          <Title>{t("create_project.step_three.title")}</Title>
          <Description2>{t("create_project.step_three.description")}</Description2>
        </HeaderContent>
      </HeaderContainer>
      <SecurityGrid>
        {[
          {
            stage: "Start-up",
            description: t("create_project.step_three.stage_options.startup.description"),
          },
          {
            stage: "Project",
            description: t("create_project.step_three.stage_options.project.description"),
          },
          {
            stage: "Proposal",
            description: t("create_project.step_three.stage_options.proposal.description"),
          },
        ].map(({ stage, description }) => (
          <SecurityCard
            key={stage}
            selected={form.values.stage === stage}
            onClick={() => form.setFieldValue("stage", stage)}
          >
            <CardHeader>
              <CardTitle>{stage}</CardTitle>
            </CardHeader>
            <CardDescription>{description}</CardDescription>
          </SecurityCard>
        ))}
        {form.errors.stage && (
          <div style={{ color: "red", marginTop: "0.5rem" }}>
            {form.errors.stage}
          </div>
        )}
      </SecurityGrid>
      {ai && (
        <>
          <HeaderContainer style={{ marginBottom: "1rem" }}>
            <HeaderContent>
              <Description2 style={{ marginTop: "3rem" }}>
                {t("create_project.step_three.ai_section.description")}
              </Description2>
            </HeaderContent>
          </HeaderContainer>
          <SecurityGrid>
            {[
              {
                roadmap: "Personalized Roadmap",
                description: t(
                  "create_project.step_three.ai_section.roadmap_options.personalized.description"
                ),
              },
              {
                roadmap: "Manual",
                description: t("create_project.step_three.ai_section.roadmap_options.manual.description"),
              },
            ].map(({ roadmap, description }) => (
              <SecurityCard
                key={roadmap}
                selected={form.values.roadmap === roadmap}
                onClick={() => form.setFieldValue("roadmap", roadmap)}
              >
                <CardHeader>
                  <CardTitle>{roadmap}</CardTitle>
                </CardHeader>
                <CardDescription>{description}</CardDescription>
              </SecurityCard>
            ))}
            {form.errors.roadmap && (
              <div style={{ color: "red", marginTop: "0.5rem" }}>
                {form.errors.roadmap}
              </div>
            )}
          </SecurityGrid>
        </>
      )}
    </>
  );
  
  const renderStepFour = () => (
    <>
      <HeaderContainer>
        <HeaderContent>
          <Title style={{ marginTop: "2rem" }}>{t("create_project.step_four.title")}</Title>
          <Description>{t("create_project.step_four.description")}</Description>
        </HeaderContent>
      </HeaderContainer>
      <PricingContainer>
        <FormGrid>
          <FormField style={{ gridColumn: "1 / -1", maxWidth: "220px" }}>
            <Label>{t("create_project.step_four.label_start_date")}</Label>
            <DatePickerInput
              inputFormat="DD/MM/YYYY"
              value={form.values.start_date}
              onChange={(date) => {
                form.setFieldValue("start_date", date);
                form.setFieldValue("end_date", addDays(date, 10));
              }}
              error={form.errors.start_date}
              popoverProps={{
                withinPortal: true,
                position: "bottom-start",
                shadow: "md",
                withArrow: true,
              }}
              inputProps={{
                style: {
                  padding: "0.4rem 0.6rem",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#6b7280",
                  width: "100%",
                  boxSizing: "border-box",
                  background: "white",
                },
              }}
            />
          </FormField>
        </FormGrid>
        <FormGrid>
          <FormField style={{ gridColumn: "1 / -1", maxWidth: "220px" }}>
            <Label>{t("create_project.step_four.label_end_date")}</Label>
            <DatePickerInput
              size="sm"
              inputFormat="DD/MM/YYYY"
              value={form.values.end_date}
              onChange={(date) => form.setFieldValue("end_date", date)}
              minDate={
                form.values.start_date ? addDays(form.values.start_date, 10) : new Date()
              }
              error={form.errors.end_date}
              disabled={!form.values.start_date}
              popoverProps={{
                withinPortal: true,
                position: "bottom-start",
                shadow: "md",
                withArrow: true,
              }}
              inputProps={{
                style: {
                  padding: "0.4rem 0.6rem",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#6b7280",
                  width: "100%",
                  boxSizing: "border-box",
                  background: "white",
                },
              }}
            />
          </FormField>
        </FormGrid>
        {form.errors.start_date && (
          <div style={{ color: "red", marginTop: "0.3rem" }}>
            {form.errors.start_date}
          </div>
        )}
      </PricingContainer>
    </>
  );
  
  const renderStepFive = () => (
    <>
      <HeaderContainer style={{ marginTop: "10px", marginBottom: "10px" }}>
        <HeaderContent>
          <Title style={{ marginTop: "2rem" }}>{t("create_project.step_five.title")}</Title>
          <Description>{t("create_project.step_five.description")}</Description>
        </HeaderContent>
      </HeaderContainer>
      <EmailSection>
        <FormGrid>
          <FormField style={{ gridColumn: "1 / -1" }}>
            <Label>{t("create_project.step_five.label_industry")}</Label>
            <Select
              value={form.values.industry}
              onChange={(e) => form.setFieldValue("industry", e.target.value)}
              error={form.errors.industry}
            >
              <option value="">{t("create_project.step_five.placeholder_industry")}</option>
              {industriesData.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField>
            <Label>{t("create_project.step_five.label_country")}</Label>
            <Select
              value={form.values.country}
              onChange={(e) => form.setFieldValue("country", e.target.value)}
              error={form.errors.country}
            >
              <option value="">{t("create_project.step_five.placeholder_country")}</option>
              {countriesData.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField>
            <Label>{t("create_project.step_five.label_city")}</Label>
            <FocusInput
              type="text"
              placeholder={t("create_project.step_five.placeholder_city")}
              {...form.getInputProps("city")}
              error={form.errors.city}
            />
          </FormField>
        </FormGrid>
        <CheckboxGroup>
          <Checkbox
            type="checkbox"
            checked={form.values.public_listed}
            onChange={(e) => form.setFieldValue("public_listed", e.target.checked)}
          />
          <CheckboxLabel>{t("create_project.step_five.checkbox_public")}</CheckboxLabel>
        </CheckboxGroup>
        <HelperText style={{ marginTop: "0" }}>
          {t("create_project.step_five.helper_private")}
        </HelperText>
      </EmailSection>
    </>
  );
  
  const renderStepSix = () => (
    <>
      <HeaderContainer style={{ marginBottom: "1rem" }}>
        <HeaderContent>
          <Title style={{ marginTop: "2rem" }}>{t("create_project.step_six.title")}</Title>
          <Description>{t("create_project.step_six.description")}</Description>
        </HeaderContent>
      </HeaderContainer>
      <EmailSection>
        <FormGrid>
          <FormField style={{ gridColumn: "1 / -1" }}>
            <Label>{t("create_project.step_six.label_website")}</Label>
            <FocusInput
              type="text"
              placeholder={t("create_project.step_six.placeholder_website")}
              {...form.getInputProps("website")}
            />
            {form.errors.website && (
              <div style={{ color: "red", marginTop: "0.5rem" }}>
                {form.errors.website}
              </div>
            )}
          </FormField>
          <FormField>
            <Label>{t("create_project.step_six.label_linkedin")}</Label>
            <FocusInput
              type="text"
              placeholder={t("create_project.step_six.placeholder_linkedin")}
              {...form.getInputProps("linkedin")}
            />
            {form.errors.linkedin && (
              <div style={{ color: "red", marginTop: "0.5rem" }}>
                {form.errors.linkedin}
              </div>
            )}
          </FormField>
          <FormField>
            <Label>{t("create_project.step_six.label_social_media")}</Label>
            <FocusInput
              type="text"
              placeholder={t("create_project.step_six.placeholder_social_media")}
              {...form.getInputProps("social_media")}
            />
            {form.errors.social_media && (
              <div style={{ color: "red", marginTop: "0.5rem" }}>
                {form.errors.social_media}
              </div>
            )}
          </FormField>
        </FormGrid>
      </EmailSection>
    </>
  );
  

  // --- End of renderStep functions ---

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = form.validate();
    if (Object.keys(isValid.errors).length > 0) {
      setNotification({
        type: "failure",
        content: "Please fill all required fields before submitting.",
      });
      return;
    }
    setLoading(true);
    try {
      const toDateOnly = (d) =>
        d instanceof Date && !isNaN(d)
          ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
          : null;

      const payloadValues = {
        ...form.values,
        start_date: toDateOnly(form.values.start_date),
        end_date: toDateOnly(form.values.end_date),
      };

      const response = await fetchApi("/startup_entities", "POST", {
        startup_entity: {
          ...payloadValues,
          description: cleanText(form.values.description),
          details: cleanText(form.values.details),
        },
      });
      if (response.ok) {
        const res = await response.json();
        sessionStorage.setItem(
          "roadmap_payload",
          JSON.stringify(res.roadmap_payload)
        );
        window.location.assign(res.redirect_path);
      } else {
        const msg = (await response.json()).error;
        setNotification({ type: "failure", content: msg });
        setLoading(false);
      }
    } catch (error) {
      console.error("Error during submission:", error);
      setNotification({ type: "failure", content: "An error occurred" });
      setLoading(false);
    }
  };

  // Render all steps in order
  return (
    <FormContainer as="form" onSubmit={handleSubmit}>
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.content}
        </div>
      )}
      <Caption href="/dashboard" onClick={()=> window.history.back()}>Go back</Caption>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {/* Step 1: Name Your Vision */}
        {renderStepOne()}
        {/* Step 2: Describe It in a Sentence */}
        {renderStepTwo()}
        {/* Step 3: Additional Details */}
        {renderStepThree()}
        {/* Step 4: What Stage Are You At? */}
        {renderStepFour()}
        {/* Step 5: Select Preferred Roadmap Option */}
        {renderStepFive()}
        {/* Step 6: Social Links (Optional) */}
        {renderStepSix()}
      </div>
      <Footer>
        <Button variant="primary" type="submit" disabled={loading} style={{ width: "100%", fontSize: "1.1rem", padding: "1rem 0" }}>
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </Footer>
    </FormContainer>
  );
};

// 1. Add FocusInput and FocusTextArea components
const FocusInput = ({ placeholder, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  return (
    <Input
      {...props}
      placeholder={isFocused ? placeholder : ""}
      onFocus={(e) => {
        setIsFocused(true);
        props.onFocus && props.onFocus(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        props.onBlur && props.onBlur(e);
      }}
    />
  );
};

const FocusTextArea = ({ placeholder, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  return (
    <StyledTextArea
      {...props}
      placeholder={isFocused ? placeholder : ""}
      onFocus={(e) => {
        setIsFocused(true);
        props.onFocus && props.onFocus(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        props.onBlur && props.onBlur(e);
      }}
    />
  );
};

export default MultiStepForm;
